import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/SiteNav";
import { SiteFooter } from "@/components/SiteFooter";
import type { AccordionBlock } from "@/components/subpages/AccordionSection";
import { ComparisonModulesGrid } from "@/components/subpages/ComparisonModulesGrid";
import { ComparisonTable } from "@/components/subpages/ComparisonTable";
import { FaqList } from "@/components/subpages/FaqList";

const vergleichHeroUrl = "/__l5e/assets-v1/37702440-82a0-41cb-a428-fa4549d1b337/vergleich-hero.jpg";

export const Route = createFileRoute("/was-vergleichen-wir")({
  head: () => ({
    meta: [
      { title: "Was vergleichen wir? – 13 Vergleichsmodule von Energy Prime 360" },
      {
        name: "description",
        content:
          "Angebote, Hersteller, PV, Speicher, Wechselrichter, Wärmepumpen, Fördermittel, Finanzierungen, Steuer und Wirtschaftlichkeit – objektiv vergleichbar gemacht.",
      },
      { property: "og:title", content: "Was vergleichen wir für Sie?" },
      { property: "og:description", content: "Angebote, Hersteller, Kosten, Qualität, Förderung – 10/20/30 Jahre im Vergleich." },
      { property: "og:url", content: "https://www.energie-prime.de/was-vergleichen-wir" },
      { property: "og:image", content: vergleichHeroUrl },
      { name: "twitter:image", content: vergleichHeroUrl },
    ],
    links: [{ rel: "canonical", href: "https://www.energie-prime.de/was-vergleichen-wir" }],
  }),
  component: Page,
});

const blocks: AccordionBlock[] = [
  { id: "angebote", title: "Angebotsvergleich", explanation: "Eingereichte Angebote werden auf Leistung, Komponenten, Garantien und Preis verglichen.", benefit: "Sie sehen, welches Angebot tatsächlich das beste ist.", example: "3 PV-Angebote → Differenz 8.400 € bei gleicher Leistung.", bullets: ["Leistungs­umfang", "Garantien", "Endpreis", "Versteckte Kosten"] },
  { id: "hersteller", title: "Herstellervergleich", explanation: "Vergleich nach Qualität, Garantie, Insolvenz­risiko, Service­netz.", benefit: "Langlebigkeit statt Billig­ware.", example: "Marke A: 25 J Garantie, B: 12 J – relevant für 30-J-Rechnung.", bullets: ["Qualität", "Garantie", "Service", "Bonität"] },
  { id: "pv", title: "Photovoltaikvergleich", explanation: "Module nach Wirkungsgrad, Degradation, Temperatur­koeffizient.", benefit: "Mehr Ertrag pro m².", example: "21,5 % vs. 19,8 % → +540 kWh/a bei 8 kWp.", bullets: ["Wirkungsgrad", "Degradation", "Temp-Koeff.", "Garantie"] },
  { id: "speicher", title: "Speichervergleich", explanation: "Kapazität, nutzbare Energie, Zyklen, Wirkungsgrad, Notstrom.", benefit: "Richtige Größe, richtige Technologie.", example: "LFP 8 kWh, 10.000 Zyklen vs. NMC 8 kWh, 6.000 Zyklen.", bullets: ["Chemie", "Zyklen", "DoD", "Notstrom"] },
  { id: "wr", title: "Wechselrichtervergleich", explanation: "Wirkungsgrad, MPP-Tracker, Notstrom, Monitoring.", benefit: "Höherer Systemertrag.", example: "WR 98,2 % vs. 96,8 % → +120 kWh/a.", bullets: ["Wirkungsgrad", "MPP", "Notstrom", "Monitoring"] },
  { id: "systemfaehig", title: "Systemfähigkeitsvergleich", explanation: "Kompatibilität PV + Speicher + WP + Wallbox + EMS.", benefit: "Zukunftssicher statt Insel­lösung.", example: "Hersteller X komplett integrierbar, Y nicht.", bullets: ["EMS", "WP-Anbindung", "Wallbox", "API"] },
  { id: "verluste", title: "Energieverlustvergleich", explanation: "Reale System­verluste gegenüber Werbe­prospekten.", benefit: "Belastbare Ertrags­prognose.", example: "Marketing 9.500 kWh → real 7.800 kWh.", bullets: ["Leitung", "MPP", "Speicher", "Temperatur"] },
  { id: "wp", title: "Wärmepumpenvergleich", explanation: "JAZ, Schall, Kältemittel, Modulation, Förder­fähigkeit.", benefit: "Richtige WP für Ihr Haus.", example: "Luft JAZ 3,8 vs. Sole 4,6 – Mehr­invest 9.000 €.", bullets: ["JAZ", "Schall", "Kältemittel", "Modulation"] },
  { id: "foerder", title: "Fördermittelvergleich", explanation: "Welche Programme sind kombinierbar, welche schließen sich aus.", benefit: "Maximale Förder­quote.", example: "BEG EM 30 % + KfW 270 + Landeszuschuss 1.500 €.", bullets: ["KfW", "BAFA", "Land", "Steuer"] },
  { id: "finanz", title: "Finanzierungsvergleich", explanation: "Förderkredit, Hausbank, Leasing, Contracting.", benefit: "Beste Konditionen + Liquidität.", example: "Förderkredit 2,1 % vs. Hausbank 4,3 %.", bullets: ["Zins", "Laufzeit", "Sonder­tilgung", "Tilgungs­zuschuss"] },
  { id: "steuer", title: "Steuervergleich", explanation: "§35c vs. AfA vs. 0%-USt vs. IAB – beste Variante je Konstellation.", benefit: "Optimaler Steuer­hebel.", example: "Eigennutz: §35c. Vermietung: AfA + IAB.", bullets: ["§35c", "AfA", "0% USt", "IAB"] },
  { id: "wirtschaft", title: "Wirtschaftlichkeitsvergleich", explanation: "Varianten A–E im direkten TCO-Vergleich.", benefit: "Die wirtschaftlich beste Entscheidung wird sichtbar.", example: "Variante C: IRR 6,8 % schlägt B (4,1 %) und D (5,9 %).", bullets: ["TCO 10/20/30", "IRR", "NPV", "Pay-Back"] },
  { id: "zukunft", title: "Zukunftsvergleich 10 / 20 / 30 Jahre", explanation: "Energie­preise, Regulatorik, Technologie – Szenario­vergleich.", benefit: "Robuste Entscheidung über Jahrzehnte.", example: "Strompreis +3 % p.a. → Variante E vorn.", bullets: ["Preis­szenarien", "Regulatorik", "Reinvest", "Restwert"] },
];

const exampleColumns = ["Angebot A", "Angebot B", "Angebot C"];
const exampleRows = [
  { criterion: "Leistung (kWp)", cells: ["8,2", "7,6", "8,0"] },
  { criterion: "Speicher (kWh)", cells: ["8,0", "5,0", "10,0"] },
  { criterion: "Modul­garantie", cells: ["25 J", "12 J", "20 J"] },
  { criterion: "WR-Garantie", cells: ["10 J", "5 J", "10 J"] },
  { criterion: "Endpreis brutto", cells: ["24.800 €", "19.900 €", "27.300 €"] },
  { criterion: "€ / kWp", cells: ["1.490", "1.580", "1.610"] },
  { criterion: "Förderung (geschätzt)", cells: ["− 3.200 €", "− 1.800 €", "− 4.100 €"] },
  { criterion: "Netto-Investition", cells: ["21.600 €", "18.100 €", "23.200 €"] },
  { criterion: "Stromkosten gespart / Jahr", cells: ["1.480 €", "1.020 €", "1.690 €"] },
  { criterion: "Kosten nach 10 Jahren *", cells: ["6.800 €", "7.900 €", "6.300 €"] },
  { criterion: "Kosten nach 20 Jahren *", cells: ["− 7.900 €", "− 2.300 €", "− 10.400 €"] },
  { criterion: "Kosten nach 30 Jahren *", cells: ["− 22.600 €", "− 12.500 €", "− 27.100 €"] },
  { criterion: "Energiekosten 30 J. OHNE Sanierung **", cells: ["66.500 €", "66.500 €", "66.500 €"] },
  { criterion: "Energiekosten 30 J. MIT diesem Angebot **", cells: ["43.900 €", "54.000 €", "39.400 €"] },
  { criterion: "Ersparnis gegenüber Nichtstun über 30 Jahre", cells: ["22.600 €", "12.500 €", "27.100 €"] },
  { criterion: "Empfehlung", cells: ["Solide", "Günstig, aber kurz", "Beste 30-J-Bilanz"] },
];

function Check({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 text-sm text-navy/85">
      <span className="inline-flex w-5 h-5 rounded-full bg-[color-mix(in_oklab,var(--leaf)_15%,white)] items-center justify-center shrink-0">
        <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-[var(--leaf-deep)]" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </span>
      <span>{children}</span>
    </div>
  );
}

type Feature = { title: string; body: string; color: string; icon: React.ReactNode };

const features: Feature[] = [
  {
    title: "Angebote, Hersteller & Produkte",
    body: "Wir vergleichen objektiv verschiedene Angebote, führende Hersteller und die dazugehörigen Produkte – passend zu Ihren Anforderungen und Zielen.",
    color: "bg-navy",
    icon: (
      <svg viewBox="0 0 24 24" className="w-7 h-7 text-white" fill="currentColor" aria-hidden>
        <path d="M9 2h6a1 1 0 011 1v1h3a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2h3V3a1 1 0 011-1zm0 2v2h6V4H9zm-2 8h10v2H7v-2zm0 4h7v2H7v-2z" />
      </svg>
    ),
  },
  {
    title: "Kosten, Qualität & Leistungen",
    body: "Verstehen Sie die wahren Kosten über die gesamte Laufzeit und bewerten Sie Qualität, Effizienz und inkludierte Leistungen im Detail.",
    color: "bg-[var(--leaf-deep)]",
    icon: (
      <svg viewBox="0 0 24 24" className="w-7 h-7 text-white" fill="currentColor" aria-hidden>
        <path d="M12 2l3 6 6 .9-4.5 4.3 1 6.3L12 16.8 6.5 19.5l1-6.3L3 8.9 9 8l3-6z" />
      </svg>
    ),
  },
  {
    title: "Förderungen, Finanzierungen & Steuern",
    body: "Wir berücksichtigen alle relevanten Förderungen, Finanzierungsmodelle und steuerlichen Effekte, um Ihre Nettoinvestition zu optimieren.",
    color: "bg-orange",
    icon: (
      <svg viewBox="0 0 24 24" className="w-7 h-7 text-white" fill="currentColor" aria-hidden>
        <path d="M3 21V10h4v11H3zm7 0V3h4v18h-4zm7 0v-7h4v7h-4zM2 22h20v-1H2v1z" />
      </svg>
    ),
  },
  {
    title: "10, 20 & 30 Jahre im Vergleich",
    body: "Wir rechnen 10, 20 und 30 Jahre voraus: Kosten, Erträge, Einsparungen und Wertentwicklung – für maximale Planungssicherheit.",
    color: "bg-[#7e3af2]",
    icon: (
      <svg viewBox="0 0 24 24" className="w-7 h-7 text-white" fill="currentColor" aria-hidden>
        <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm1 5h-2v6l5 3 1-1.7-4-2.3V7z" />
      </svg>
    ),
  },
];

type Vorteil = { title: string; sub: string; icon: React.ReactNode };

const vorteile: Vorteil[] = [
  {
    title: "Transparenz schaffen",
    sub: "Alle Angebote und Faktoren klar und verständlich.",
    icon: (
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor" aria-hidden>
        <path d="M12 5c-7 0-11 7-11 7s4 7 11 7 11-7 11-7-4-7-11-7zm0 11a4 4 0 110-8 4 4 0 010 8z" />
      </svg>
    ),
  },
  {
    title: "Preis-Leistung bewerten",
    sub: "Das beste Verhältnis von Kosten und Nutzen finden.",
    icon: (
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor" aria-hidden>
        <path d="M12 2L1 8l3 1v8l8 5 8-5V9l3-1-11-6zm0 2.3L20 8l-8 3.7L4 8l8-3.7z" />
      </svg>
    ),
  },
  {
    title: "Qualität vergleichen",
    sub: "Produkte, Leistungen und Garantien objektiv prüfen.",
    icon: (
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor" aria-hidden>
        <path d="M12 1l3.1 6.3 6.9 1-5 4.9 1.2 6.9L12 17l-6.2 3.1L7 13.2 2 8.3l6.9-1L12 1z" />
      </svg>
    ),
  },
  {
    title: "Langfristig rechnen",
    sub: "10, 20 & 30 Jahre für nachhaltige Entscheidungen.",
    icon: (
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor" aria-hidden>
        <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm1 5h-2v6l5 3 1-1.7-4-2.3V7z" />
      </svg>
    ),
  },
  {
    title: "Die beste Lösung finden",
    sub: "Sicher entscheiden und Zukunft optimal gestalten.",
    icon: (
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor" aria-hidden>
        <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm0 4a6 6 0 110 12 6 6 0 010-12zm0 3a3 3 0 100 6 3 3 0 000-6z" />
      </svg>
    ),
  },
];

function Page() {
  return (
    <div className="min-h-screen bg-[#f4f6fa] text-navy">
      <SiteNav />
      <main>
        {/* HERO */}
        <section className="border-b border-[var(--rule)]">
          <div className="mx-auto max-w-[1280px] px-5 md:px-10 py-10 md:py-16 grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <h1 className="font-display font-bold text-navy text-3xl md:text-5xl leading-[1.05]">
                Was vergleichen wir<br />
                für <span className="text-orange">Sie?</span>
              </h1>
              <p className="mt-5 text-navy/80 text-base md:text-lg leading-relaxed max-w-[520px]">
                Wir vergleichen für Sie Angebote, Hersteller und Produkte objektiv und transparent –
                inklusive Kosten, Qualität, Leistungen, Förderungen, Finanzierungen und Steuervorteilen.
              </p>
              <p className="mt-3 text-navy/80 text-base md:text-lg leading-relaxed max-w-[520px]">
                Vergleichen Sie 10, 20 und 30 Jahre im Voraus und treffen Sie die beste Entscheidung
                für Ihr Projekt.
              </p>
              <div className="mt-6 flex flex-col gap-3">
                <Check>Angebote &amp; Hersteller</Check>
                <Check>Kosten &amp; Qualität</Check>
                <Check>10, 20 &amp; 30 Jahre Vergleich</Check>
              </div>
            </div>
            <div>
              <img
                src={vergleichHeroUrl}
                alt="Persönliche Analyse mit Vergleich von Eigenverbrauch, Investition, Amortisation und Ersparnisentwicklung"
                width={1280}
                height={960}
                className="w-full h-auto rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </section>

        {/* FEATURE CARDS */}
        <section className="border-b border-[var(--rule)]">
          <div className="mx-auto max-w-[1280px] px-5 md:px-10 py-12 md:py-16">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {features.map((f) => (
                <article
                  key={f.title}
                  className="bg-white rounded-2xl border border-[var(--rule)] shadow-sm p-6 flex flex-col items-center text-center hover:shadow-md transition"
                >
                  <span className={`w-14 h-14 rounded-full ${f.color} inline-flex items-center justify-center mb-4 shadow-sm`}>
                    {f.icon}
                  </span>
                  <h3 className="font-display font-semibold text-navy text-base leading-snug mb-3 min-h-[3rem]">
                    {f.title}
                  </h3>
                  <p className="text-sm text-navy/70 leading-relaxed">{f.body}</p>
                  <span className="mt-4 text-[var(--leaf-deep)] text-xl" aria-hidden>→</span>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* VORTEILE STRIP */}
        <section className="border-b border-[var(--rule)]" style={{ backgroundColor: "#eef1f5" }}>
          <div className="mx-auto max-w-[1280px] px-5 md:px-10 py-10 md:py-12">
            <h2 className="text-center font-display font-bold text-navy text-xl md:text-2xl mb-8">
              Ihre Vorteile auf einen Blick
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-5">
              {vorteile.map((v) => (
                <div key={v.title} className="flex items-start gap-3 bg-white rounded-xl border border-[var(--rule)] p-4">
                  <span className="text-[var(--leaf-deep)] shrink-0 mt-0.5">{v.icon}</span>
                  <div className="min-w-0">
                    <div className="font-display font-semibold text-navy text-sm leading-tight">{v.title}</div>
                    <div className="text-xs text-navy/65 mt-1 leading-snug">{v.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 13 Vergleichsmodule */}
        <ComparisonModulesGrid blocks={blocks} />

        {/* Beispielhafter Angebotsvergleich */}
        <section className="border-b border-[var(--rule)] surface-linen">
          <div className="mx-auto max-w-[1280px] px-5 md:px-10 py-14 md:py-20">
            <div className="eyebrow text-[var(--leaf)] mb-3">Beispiel</div>
            <h2 className="font-display font-bold text-2xl md:text-4xl text-[var(--ink)] mb-3">
              Beispielhafter Angebotsvergleich
            </h2>
            <p className="text-navy/75 text-base max-w-2xl mb-6">
              Drei reale Angebote für dasselbe Einfamilienhaus – inklusive Förderung,
              Stromersparnis und Kostenbilanz nach 10, 20 und 30 Jahren.
            </p>
            <ComparisonTable
              columns={exampleColumns}
              rows={exampleRows}
              caption="Vergleich PV + Speicher · EFH Beispielprojekt (8 kWp · 4-Personen-Haushalt)"
            />
            <p className="mt-4 text-xs text-[var(--ink)]/65 leading-relaxed">
              * Kostenbilanz = Investition − Stromersparnis − Förderung + Wartung/Reinvest.
              Negative Werte = Sie haben über den Zeitraum Geld <strong>verdient</strong>.
              Annahmen: Strompreis 36 ct/kWh, Steigerung 3 % p.a., Eigenverbrauchsquote
              ~ 70 % mit Speicher. Bewertung: Leistung, Qualität, Garantien und €/kWp
              werden zu einem Score 0–100 verdichtet.
            </p>
          </div>
        </section>


        <FaqList
          items={[
            { q: "Sind Hersteller­vergleiche objektiv?", a: "Ja. Energy Prime hat keine Hersteller­bindung und keine Provisionen." },
            { q: "Auf welcher Basis erfolgt der Preis­vergleich?", a: "Aktuelle Marktpreis-Bandbreiten je Komponenten­klasse und Region." },
          ]}
        />

        {/* CTA */}
        <section>
          <div className="mx-auto max-w-[1280px] px-5 md:px-10 py-12 md:py-16 grid md:grid-cols-[1.1fr_1fr] gap-10 items-center">
            <div>
              <h2 className="font-display font-bold text-navy text-2xl md:text-3xl leading-tight">
                Bereit für den besten <span className="text-[var(--leaf-deep)]">Vergleich</span>?
              </h2>
              <p className="mt-4 text-navy/80 text-base leading-relaxed max-w-[540px]">
                Laden Sie Ihre Angebote hoch oder starten Sie direkt einen Vergleich. Wir analysieren
                und vergleichen alle relevanten Faktoren – objektiv, unabhängig und für Ihre beste Entscheidung.
              </p>
              <div className="mt-5 flex flex-wrap gap-x-8 gap-y-3">
                <Check>In nur 2 Minuten starten</Check>
                <Check>100 % kostenlos &amp; unverbindlich</Check>
              </div>
              <Link
                to="/projekt-beschreiben"
                className="mt-7 inline-flex items-center gap-2 bg-orange hover:bg-orange/90 text-white font-semibold px-6 py-3 rounded-md transition shadow-md"
              >
                Vergleich starten
                <span aria-hidden>→</span>
              </Link>
            </div>
            <CtaIllustration />
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function CtaIllustration() {
  const left = [
    { label: "Stromrechnung", d: "M13 2L3 14h7l-1 8 10-12h-7l1-8z" },
    { label: "Gasrechnung", d: "M12 2s5 5 5 10a5 5 0 11-10 0c0-5 5-10 5-10z" },
    { label: "Fördermittel", d: "M12 2C7 6 5 10 5 13a7 7 0 0014 0c0-3-2-7-7-11zm0 18a5 5 0 01-5-5c0-2 1-4 2-5l3 3 3-3c1 1 2 3 2 5a5 5 0 01-5 5z" },
  ];
  const right = [
    { label: "Steuerunterlagen", d: "M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 7V3.5L18.5 9H13z" },
    { label: "Kalender", d: "M7 2h2v2h6V2h2v2h3v18H4V4h3V2zm-1 8v10h12V10H6z" },
  ];
  return (
    <div className="relative w-full max-w-md mx-auto">
      <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-center">
        <div className="flex flex-col gap-3">
          {left.map((it) => (
            <div key={it.label} className="bg-white border border-[var(--rule)] rounded-xl shadow-sm px-3 py-2.5 flex items-center gap-2">
              <span className="w-9 h-9 rounded-lg bg-[color-mix(in_oklab,var(--leaf)_12%,white)] inline-flex items-center justify-center shrink-0">
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-[var(--leaf-deep)]" fill="currentColor" aria-hidden><path d={it.d} /></svg>
              </span>
              <span className="text-[11px] md:text-xs font-semibold text-navy/80 uppercase tracking-wide">{it.label}</span>
            </div>
          ))}
        </div>

        <div className="bg-white border border-[var(--rule)] rounded-2xl shadow-lg p-4 w-[180px]">
          <div className="text-[10px] font-bold text-navy/60 uppercase tracking-wider text-center mb-2">
            Bester Vergleich<br />für Ihr Projekt
          </div>
          <ul className="space-y-1.5 text-[11px] text-navy/85">
            {["Kosten","Qualität","Service","Förderungen","Steuervorteile","Langfristiger Nutzen"].map((x) => (
              <li key={x} className="flex items-center gap-1.5">
                <span className="inline-flex w-3.5 h-3.5 rounded-sm bg-[var(--leaf-deep)] items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                </span>
                {x}
              </li>
            ))}
          </ul>
          <div className="mt-2 text-center text-[10px] font-bold text-[var(--leaf-deep)]">10 / 20 / 30 JAHRE</div>
        </div>

        <div className="flex flex-col gap-3">
          {right.map((it) => (
            <div key={it.label} className="bg-white border border-[var(--rule)] rounded-xl shadow-sm px-3 py-2.5 flex items-center gap-2">
              <span className="w-9 h-9 rounded-lg bg-[color-mix(in_oklab,var(--leaf)_12%,white)] inline-flex items-center justify-center shrink-0">
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-[var(--leaf-deep)]" fill="currentColor" aria-hidden><path d={it.d} /></svg>
              </span>
              <span className="text-[11px] md:text-xs font-semibold text-navy/80 uppercase tracking-wide">{it.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
